import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type PersonModel from 'tio-data/models/person';
import type RecommendationModel from 'tio-data/models/recommendation';
import type { LoanSummary } from 'tio-common/types/loan-summary';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type SessionContextService from 'tio-employee/services/session-context';
import { getLoanSummaryRequest } from 'tio-common/requests/user';
import { REQUESTING_APP } from 'tio-employee/utils/strategy-finder-0726';

const EMPTY_LOAN_SUMMARY: LoanSummary = { summaryByLoanType: [], errors: [] };
const STRATEGY_FINDER_APP = 'STRATEGY_FINDER';

export default class AuthenticatedStrategyFinder0726IndexRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;

  async model() {
    const userId = this.sessionContext.user.id;
    const personId = this.sessionContext.user.person.id as string;

    const [loanSummaryResult, sf0726Result, strategyFinderResult, personResult] =
      await Promise.allSettled([
        userId
          ? this.store.request(getLoanSummaryRequest(userId))
          : Promise.resolve({ content: EMPTY_LOAN_SUMMARY }),
        this.store.query<RecommendationModel>('recommendation', {
          filter: { user: userId, 'requesting-app': REQUESTING_APP },
          include: 'user',
          sort: '-created-at',
          page: { offset: 0, limit: 3 },
        }),
        this.store.query<RecommendationModel>('recommendation', {
          filter: { user: userId, 'requesting-app': STRATEGY_FINDER_APP },
          include: 'user',
          sort: '-created-at',
          page: { offset: 0, limit: 5 },
        }),
        this.store.findRecord<PersonModel>('person', personId, {
          include: 'accounts.financial-institution,accounts.loans,user.answer-profile',
          reload: true,
        }),
      ]);

    const loanSummary =
      loanSummaryResult.status === 'fulfilled'
        ? ((loanSummaryResult.value.content as LoanSummary | undefined) ?? EMPTY_LOAN_SUMMARY)
        : EMPTY_LOAN_SUMMARY;
    const sf0726Recommendations =
      sf0726Result.status === 'fulfilled' ? [...sf0726Result.value] : [];
    const strategyFinderRecommendations =
      strategyFinderResult.status === 'fulfilled' ? [...strategyFinderResult.value] : [];
    const person = personResult.status === 'fulfilled' ? personResult.value : null;

    // Combined usage history: this pilot's runs alongside the regular Strategy
    // Finder runs, newest first.
    const history = [...sf0726Recommendations, ...strategyFinderRecommendations]
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
      .slice(0, 10);

    return {
      accounts: person?.accounts ?? [],
      loanSummary,
      person,
      user: person?.user ?? null,
      mostRecentRecommendation: sf0726Recommendations[0] ?? null,
      history,
    };
  }
}
